import { Form } from "components/Form/components/Form/Form";
import InfoPanel from "components/Layout/InfoPanel";
import Placeholder from "components/Layout/Placeholder";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import {
    ActivityConfig,
    FeConfigType,
    FeResourceType,
    RelationshipConfig,
    ResourceConfig,
} from "config/config";
import { hasExecutionEndedStatus } from "config/ts-utils";
import { defaultResourcesDetailsSidebarItems } from "config/utils.jsx";
import { MoveSingleResourceToLocationAction } from "containers/Resources/components/CommonActions/MoveToLocation/MoveSingleResourceToLocation";
import { actions as resourceActions } from "containers/Resources/Resources.reducer";
import { BundleContentField } from "containers/Resources/screens/ResourcesSingle/components/ResourceDetails/BundleContentField";
import { DefaultIsBundleContentField } from "containers/Resources/screens/ResourcesSingle/components/ResourceDetails/DefaultIsBundleContentField";
import { shouldShowDefaultIsBundleContentField } from "containers/Resources/screens/ResourcesSingle/components/ResourceDetails/detailsSidebarVisibility";
import {
    ExecutionPriorityField,
    setDefaultPriorityMutator,
    useGetPriority,
} from "containers/Resources/screens/ResourcesSingle/components/ResourceDetails/ExecutionPriorityField";
import {
    MaxRuntimeMinutesField,
    useMaxRuntimeMinutes,
} from "containers/Resources/screens/ResourcesSingle/components/ResourceDetails/MaxRuntimeMinutesField";
import { ReadonlyField } from "containers/Resources/screens/ResourcesSingle/components/ResourceDetails/ReadOnlyField";
import { SavePointRetentionField } from "containers/Resources/screens/ResourcesSingle/components/ResourceDetails/SavepointRetentionField";
import { TrackInGitField } from "containers/Resources/screens/ResourcesSingle/components/ResourceDetails/TrackInGitField";
import { blurActiveElementConditionally } from "containers/Resources/screens/ResourcesSingle/utils";
import { FormApi } from "final-form";
import { ResourceType } from "graphql/generated/graphql";
import get from "lodash/get";
import { Fragment, useCallback, useMemo, useRef } from "react";
import { Form as FinalForm } from "react-final-form";
import { useDispatch } from "react-redux";
import { checkActivityType, checkContainerItemType, resourceList } from "utils/utils";
import styles from "./ResourceDetails.module.scss";
interface ResourceDetailsProps {
    resourceName: FeConfigType;
    resourceData: any;
    isFetching?: boolean;
    isDisabled?: boolean;
    resourceConfig: ResourceConfig | ActivityConfig | RelationshipConfig;
    isRecordOrContainerGitEnabled?: boolean;
    iri?: string;
    fetchResourceData?: () => void;
}

const detailsFields = {
    priority: "Execution priority",
    max_runtime_minutes: "Maximum runtime minutes",
    track_in_git: "Track in git",
    default_track_in_git: "Default track in git",
    force_track_in_git: "Force track in git",
    max_tracked_record_bytes: "Max tracked record size",
    default_is_bundle_content: "Default bundle content",
    is_bundle_content: "Bundle content",
    is_readonly: "Read only",
    savepoint_retention: "Savepoint retention",
    location: "Location",
};

type DetailsFieldKey = keyof typeof detailsFields;

interface SidebarFieldItem {
    label: string;
    valueKey: DetailsFieldKey;
    dto: () => React.ReactNode;
}

export const ResourceDetails = ({
    resourceName,
    resourceData,
    isFetching,
    isDisabled,
    resourceConfig,
    isRecordOrContainerGitEnabled,
    iri,
    fetchResourceData,
}: ResourceDetailsProps) => {
    const dispatch = useDispatch();
    const { getPriorityValue, defaultPriorityValue } = useGetPriority();
    const {
        getMaxRuntimeMinutesInitialValue,
        getMaxRuntimeMinutesSubmitValue,
        workspaceMaxRuntimeMinutesValue,
    } = useMaxRuntimeMinutes();
    const formRef = useRef<FormApi | null>(null);

    const onSubmit = useCallback(
        (formValues, form) => {
            blurActiveElementConditionally(document.activeElement?.tagName === "INPUT");
            const priority = getPriorityValue(formValues.priority);
            const max_runtime_minutes = getMaxRuntimeMinutesSubmitValue(
                formValues.max_runtime_minutes
            );

            const formValuesWithSpecialHandling = {
                ...formValues,
                priority,
                max_runtime_minutes,
            };

            const { modified } = form.getState();
            const changedFields = Object.keys(modified).filter((field) => modified[field]);
            if (changedFields.length === 0) {
                return;
            }

            const saveFields = (fields: string[]) => {
                if (fields.length === 0) {
                    return;
                }
                new Promise((resolve, reject) => {
                    dispatch({
                        type: resourceActions.START_UPDATE_RESOURCE,
                        promise: { resolve, reject },
                        resourceName,
                        iri,
                        variables: {
                            id: resourceData?.id,
                            ...fields.reduce((acc, field) => {
                                acc[field] = formValuesWithSpecialHandling[field];
                                return acc;
                            }, {}),
                        },
                    });
                })
                    .then(() => {
                        const changedFieldsText = fields
                            .map((field) => detailsFields[field] || field)
                            .join(", ");
                        NotificationService.success(`${changedFieldsText} successfully updated`);
                    })
                    .catch((error) => {
                        NotificationService.error(
                            <RestErrorNotification
                                titleOverride="Sorry, update failed"
                                error={error}
                            />
                        );
                    });
            };

            saveFields(changedFields);
        },
        [
            dispatch,
            getMaxRuntimeMinutesSubmitValue,
            getPriorityValue,
            iri,
            resourceData?.id,
            resourceName,
        ]
    );

    const isEndedExecution =
        resourceName === "execution" && hasExecutionEndedStatus(resourceData?.status);

    const sidebarItems = useMemo(() => {
        const sidebarItems = [...defaultResourcesDetailsSidebarItems];
        if (
            resourceList.includes(resourceName.toUpperCase() as ResourceType) &&
            !checkContainerItemType(resourceName)
        ) {
            sidebarItems.push({
                label: "Location",
                valueKey: "location",
                dto: () => (
                    <MoveSingleResourceToLocationAction
                        resourceName={resourceName as FeResourceType}
                        resourceData={resourceData}
                        isDisabled={isDisabled}
                        iri={iri}
                        afterAction={fetchResourceData}
                    />
                ),
            } as SidebarFieldItem);
        }
        if (
            ["execution", "flow", "wrapper", "scheduler", "connector"].some(
                (type) => resourceName === type
            )
        ) {
            sidebarItems.push({
                label: detailsFields.priority,
                valueKey: "priority",
                dto: () => (
                    <ExecutionPriorityField
                        onBlur={() => {
                            if (!formRef.current?.getState().pristine) {
                                formRef.current?.mutators.setDefaultPriority();
                                formRef.current?.submit();
                            }
                        }}
                        isDisabled={isDisabled || isEndedExecution}
                        initialValue={getPriorityValue(resourceData?.priority)}
                    />
                ),
            } as SidebarFieldItem);
            sidebarItems.push({
                label: detailsFields.max_runtime_minutes,
                valueKey: "max_runtime_minutes",
                dto: () => (
                    <MaxRuntimeMinutesField
                        workspaceMaxRuntimeMinutesValue={workspaceMaxRuntimeMinutesValue}
                        recordType={resourceName}
                        handleSubmit={() => {
                            formRef.current?.submit();
                        }}
                        isDisabled={isDisabled || isEndedExecution}
                        initialValue={getMaxRuntimeMinutesInitialValue(
                            resourceData?.max_runtime_minutes
                        )}
                    />
                ),
            } as SidebarFieldItem);
        }
        if (isRecordOrContainerGitEnabled && !checkActivityType(resourceName)) {
            // When the record's container (bundle/project) has
            // force_track_in_git=true, tracking is a non-overridable durability
            // guarantee: the backend clamps track_in_git to true regardless of
            // any per-call value. Reflect that in the control by showing it
            // read-only as "true" and naming the container that forces it,
            // mirroring the container is_readonly inheritance used below.
            const forcedByBundle = Boolean(resourceData?.bundle_id_bundle?.force_track_in_git);
            const forcedByProject = Boolean(resourceData?.project_id_project?.force_track_in_git);
            const trackInGitForced = forcedByBundle || forcedByProject;
            sidebarItems.push({
                label: detailsFields.track_in_git,
                valueKey: "track_in_git",
                dto: () => (
                    <TrackInGitField
                        onChange={() => {
                            formRef.current?.submit();
                        }}
                        isDisabled={isDisabled || trackInGitForced}
                        initialValue={
                            trackInGitForced ? true : resourceData?.track_in_git
                        }
                        label={
                            trackInGitForced
                                ? `Forced by ${forcedByBundle ? "bundle" : "project"}`
                                : undefined
                        }
                    />
                ),
            } as SidebarFieldItem);
        }
        // Container-level git settings (default track-in-git, force track-in-git,
        // max tracked record size) live in the unified "Manage git" modal
        // (GitAction/ManageGitModal), persisted together on an explicit Save; they
        // are not surfaced in this Details sidebar.
        // `default_is_bundle_content` is a bundle-only setting that is unrelated
        // to git (it governs whether new records placed in the bundle default to
        // being bundle content), so it is shown for every bundle regardless of
        // whether git is enabled for it. (See detailsSidebarVisibility.ts.)
        if (shouldShowDefaultIsBundleContentField(resourceName)) {
            sidebarItems.push({
                label: detailsFields.default_is_bundle_content,
                valueKey: "default_is_bundle_content",
                dto: () => (
                    <DefaultIsBundleContentField
                        onChange={() => {
                            formRef.current?.submit();
                        }}
                        isDisabled={isDisabled}
                        initialValue={resourceData?.default_is_bundle_content}
                    />
                ),
            } as SidebarFieldItem);
        }
        if (resourceData?.bundle_id) {
            sidebarItems.push({
                label: detailsFields.is_bundle_content,
                valueKey: "is_bundle_content",
                dto: () => (
                    <BundleContentField
                        isDisabled={isDisabled}
                        onChange={() => {
                            formRef.current?.submit();
                        }}
                        initialValue={resourceData?.is_bundle_content}
                    />
                ),
            } as SidebarFieldItem);
        }
        if (!resourceConfig.ignoreIsReadonly) {
            sidebarItems.push({
                label: detailsFields.is_readonly,
                valueKey: "is_readonly",
                dto: () => (
                    <ReadonlyField
                        isDisabled={
                            resourceData?.deleted_at ||
                            resourceData?.project_id_project?.deleted_at ||
                            resourceData?.bundle_id_bundle?.deleted_at ||
                            resourceData?.project_id_project?.is_readonly ||
                            resourceData?.bundle_id_bundle?.is_readonly
                        }
                        onChange={() => {
                            formRef.current?.submit();
                        }}
                        initialValue={resourceData?.is_readonly}
                    />
                ),
            } as SidebarFieldItem);
        }
        if (resourceName === "flow") {
            sidebarItems.push({
                label: detailsFields.savepoint_retention,
                valueKey: "savepoint_retention",
                dto: () => (
                    <SavePointRetentionField
                        initialValue={resourceData?.savepoint_retention}
                        isDisabled={isDisabled}
                        onChange={() => {
                            formRef.current?.submit();
                        }}
                    />
                ),
            } as SidebarFieldItem);
        }
        if (resourceConfig?.detailsSidebar) {
            return [...sidebarItems, ...resourceConfig.detailsSidebar];
        } else {
            return sidebarItems;
        }
    }, [
        getMaxRuntimeMinutesInitialValue,
        getPriorityValue,
        isDisabled,
        isEndedExecution,
        isRecordOrContainerGitEnabled,
        resourceConfig.detailsSidebar,
        resourceConfig.ignoreIsReadonly,
        resourceData,
        resourceName,
        workspaceMaxRuntimeMinutesValue,
        iri,
        fetchResourceData,
    ]);

    return (
        <>
            <InfoPanel className={`resourceDetails ${styles.detailsPanelForm}`}>
            <InfoPanel.Title>Details</InfoPanel.Title>
            {isFetching ? (
                <Placeholder.List />
            ) : (
                <FinalForm
                    onSubmit={onSubmit}
                    mutators={{
                        setDefaultPriority: (args, state, utils) =>
                            setDefaultPriorityMutator({ state, utils, defaultPriorityValue }),
                    }}
                >
                    {({ handleSubmit, form }) => {
                        formRef.current = form;
                        return (
                            <Form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleSubmit();
                                }}
                            >
                                {sidebarItems?.map((item) => {
                                    const value = item.dto?.(
                                        item.valueKey
                                            ? get(resourceData, item.valueKey)
                                            : resourceData
                                    );
                                    if (value == null || value === "") return null;
                                    return (
                                        <Fragment key={`${item.label}${item.valueKey}`}>
                                            <InfoPanel.Label>{item.label}</InfoPanel.Label>
                                            <InfoPanel.Value>{value}</InfoPanel.Value>
                                        </Fragment>
                                    );
                                })}
                            </Form>
                        );
                    }}
                </FinalForm>
            )}
            </InfoPanel>
        </>
    );
};
